import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Calendar, ExternalLink, Globe } from "lucide-react";
import { getCourseGroupBySlug } from "../data/courseGroups";
import WaitlistForm from "./kurser/WaitlistForm";

const Row = ({ label, value, valueClass = "" }) => (
  <div className="flex justify-between gap-4">
    <span className="text-slate-500">{label}</span>
    <span className={`font-medium text-right ${valueClass}`}>{value || "-"}</span>
  </div>
);

const UcplusCourseList = ({ course, votraSessions = [], onVotraSignup }) => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [languageFilter, setLanguageFilter] = useState("all");

  const amuPrice = getCourseGroupBySlug(course)?.amuPrice;

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        setLoading(true);
        setErrorMessage("");
        setLanguageFilter("all");
        const res = await axios.get(`${API}/ucplus-hold`, { params: { course } });
        if (!res.data?.success) {
          throw new Error(res.data?.message || "Kunne ikke hente hold");
        }
        if (active) setCourses(res.data.courses || []);
      } catch (e) {
        console.error("Kunne ikke hente UCplus hold:", e);
        if (active) setErrorMessage("Vi kunne ikke hente hold lige nu. Prøv igen senere.");
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [course]);

  if (loading) {
    return (
      <div className="py-8 text-center" data-testid="ucplus-loading">
        <div className="inline-block h-7 w-7 animate-spin rounded-full border-4 border-solid border-[hsl(210,100%,45%)] border-r-transparent"></div>
        <p className="mt-3 text-slate-600">Henter kommende hold...</p>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-5 text-center" data-testid="ucplus-error">
        <p className="text-red-700">{errorMessage}</p>
      </div>
    );
  }

  if (!courses.length && !votraSessions.length) {
    return (
      <WaitlistForm slug={course} title={getCourseGroupBySlug(course)?.title || course} />
    );
  }

  // Merge Votra-created holds (registration on votra.dk) with UCplus API holds,
  // rendered with identical design; Votra holds carry a VOTRA badge.
  const allCourses = [...votraSessions, ...courses];

  // Distinct languages present across the holds (for the language filter)
  const languages = Array.from(new Set(allCourses.map(c => c.language).filter(Boolean)));
  const visibleCourses = languageFilter === "all"
    ? allCourses
    : allCourses.filter(c => c.source === "votra" || c.language === languageFilter);

  return (
    <div data-testid="ucplus-hold-list">
      <h2 className="text-xl font-semibold mb-1" style={{ color: 'hsl(222.2, 47.4%, 11.2%)' }}>
        Kommende hold
      </h2>
      <p className="text-sm text-slate-500 mb-5">
        Vælg et hold nedenfor. Hold markeret <span className="font-semibold text-[hsl(210,100%,45%)]">VOTRA</span> tilmeldes direkte her; øvrige hold via voksenuddannelse.dk.
      </p>

      {/* Language filter (only shown when more than one language is available) */}
      {languages.length > 1 && (
        <div className="mb-5 flex flex-wrap items-center gap-2" data-testid="ucplus-language-filter">
          <span className="flex items-center gap-1.5 text-sm font-medium text-slate-600 mr-1">
            <Globe className="h-4 w-4" />
            Sprog:
          </span>
          <button
            onClick={() => setLanguageFilter("all")}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              languageFilter === "all"
                ? "bg-[hsl(210,100%,45%)] text-white"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
            data-testid="ucplus-language-all"
          >
            Alle
          </button>
          {languages.map((lang) => (
            <button
              key={lang}
              onClick={() => setLanguageFilter(lang)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                languageFilter === lang
                  ? "bg-[hsl(210,100%,45%)] text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
              data-testid={`ucplus-language-${lang}`}
            >
              {lang}
            </button>
          ))}
        </div>
      )}

      {visibleCourses.length === 0 ? (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-5 text-center text-slate-600" data-testid="ucplus-empty-filtered">
          Ingen hold på det valgte sprog.
        </div>
      ) : (
      <div className="grid gap-4 md:grid-cols-2">
        {visibleCourses.map((c, idx) => {
          const isVotra = c.source === "votra";
          return (
          <div
            key={`${c.kviknummer || c.id}-${idx}`}
            className="relative rounded-xl border border-slate-200 p-5 hover:border-[hsl(210,100%,45%)] transition-colors"
            data-testid={isVotra ? `votra-hold-${c.id}` : `ucplus-hold-${c.kviknummer || c.id}`}
          >
            {isVotra ? (
              <span
                className="absolute top-4 right-4 inline-flex items-center rounded-full bg-[hsl(210,100%,45%)] px-2.5 py-1 text-xs font-bold tracking-wide text-white shadow-sm"
                data-testid={`votra-badge-${c.id}`}
              >
                VOTRA
              </span>
            ) : (
              <span
                className="absolute top-4 right-4 inline-flex items-center rounded-full bg-slate-800 px-2.5 py-1 text-xs font-bold tracking-wide text-white shadow-sm"
                title="Holdet afholdes af vores partner UCplus"
                data-testid={`ucplus-badge-${c.kviknummer || c.id}`}
              >
                UCPLUS
              </span>
            )}
            <div className="flex items-center gap-2 mb-3 pr-16">
              <Calendar className="h-4 w-4 text-[hsl(210,100%,45%)]" />
              <span className="text-lg font-bold text-slate-900">
                {c.startDate || "Dato ikke oplyst"}
              </span>
            </div>

            <div className="space-y-2 text-sm text-slate-700">
              <Row label="Slutdato" value={c.endDate} />
              <Row label="Lokation" value={c.location} />
              <Row label="Undervisning" value={c.teachingForm} />
              <Row label="Sprog / forudsætninger" value={c.language} />
              <Row
                label="Ledige pladser"
                value={c.isFull ? "Fuldt booket" : c.availableSeats}
                valueClass={c.isFull ? "text-red-600 font-bold" : "text-green-700 font-bold"}
              />
              {isVotra ? (
                (c.price || c.price === 0) && (
                  <Row
                    label="Pris"
                    value={`${Number(c.price).toLocaleString('da-DK')} kr.`}
                    valueClass="text-[hsl(210,100%,45%)] font-bold"
                  />
                )
              ) : (
                amuPrice && (
                  <Row
                    label="Pris"
                    value={`${amuPrice.toLocaleString('da-DK')} kr.`}
                    valueClass="text-[hsl(210,100%,45%)] font-bold"
                  />
                )
              )}
              {!isVotra && <Row label="Tilmeldingsfrist" value={c.registrationDeadline} />}
              {isVotra
                ? <>
                    <Row label="Tilmeldingsfrist" value={c.registrationDeadline} />
                    <Row label="Hold nummer" value={c.holdNummer} />
                  </>
                : <Row label="Kviknummer" value={c.kviknummer} />}
            </div>

            {isVotra ? (
              <button
                onClick={() => !c.isFull && onVotraSignup && onVotraSignup(c._raw)}
                disabled={c.isFull}
                className={`mt-5 w-full rounded-lg px-4 py-3 font-semibold transition-colors ${
                  c.isFull
                    ? "bg-slate-200 text-slate-500 cursor-not-allowed"
                    : "bg-[hsl(210,100%,45%)] text-white hover:bg-[hsl(210,100%,40%)]"
                }`}
                data-testid={`votra-signup-${c.id}`}
              >
                {c.isFull ? "Fuldt booket" : "Tilmeld dig"}
              </button>
            ) : c.signupUrl ? (
              <a
                href={c.signupUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 flex items-center justify-center gap-2 w-full rounded-lg bg-[hsl(210,100%,45%)] px-4 py-3 font-semibold text-white transition-colors hover:bg-[hsl(210,100%,40%)]"
                data-testid={`ucplus-signup-${c.kviknummer || c.id}`}
              >
                Tilmeld dig <ExternalLink className="h-4 w-4" />
              </a>
            ) : (
              <button
                disabled
                className="mt-5 w-full rounded-lg bg-slate-200 px-4 py-3 font-semibold text-slate-500 cursor-not-allowed"
              >
                Tilmelding ikke tilgængelig
              </button>
            )}
          </div>
          );
        })}
      </div>
      )}
    </div>
  );
};

export default UcplusCourseList;
